import { DESIGN_W, DESIGN_H } from './theme';

/**
 * Live design-space viewport. In **portrait** (the default for every scene) the world is fit to
 * **width** (always 720 wide), so the horizontal axis is fixed and the **height** is variable: on a
 * screen taller than 9:16 (e.g. a phone in Telegram fullscreen) it grows past the 1280 baseline so
 * scenes can fill the space instead of being letterboxed.
 *
 * In **landscape** (only `BackgammonTableScene`, which the app auto-rotates 90° to fill a
 * portrait-held phone) the axes swap: the **height** becomes the fixed short axis (720) and the
 * **width** becomes the variable long axis. `PixiApp.layout()` publishes both via {@link setViewport};
 * scenes/helpers read {@link viewport}.w / .h and re-flow on {@link onViewportChange}.
 */
let _w = DESIGN_W;
let _h = DESIGN_H;

// Which way the *active* scene wants to render. SceneManager publishes this from `scene.orientation`
// before each relayout; PixiApp.layout() reads it to pick the portrait vs landscape (rotated) branch.
let _orientation: 'portrait' | 'landscape' = 'portrait';

// Device/Telegram safe-area insets in **screen pixels** (notch/camera + Telegram's top buttons up top,
// rounded corner / home indicator at the bottom). PixiApp.layout() subtracts these so the design space
// maps into the safe region; the dark app-background chrome shows in the inset strips. 0 outside Telegram.
let _insetTop = 0;
let _insetBottom = 0;

export const viewport = {
  /** Design width — 720 (fixed) in portrait; the variable long axis in landscape. */
  get w(): number {
    return _w;
  },
  /** Design height — `DESIGN_H` (1280) baseline (larger on tall screens) in portrait; 720 in landscape. */
  get h(): number {
    return _h;
  },
  /** Horizontal center — `w/2` (360 in portrait). */
  get cx(): number {
    return _w / 2;
  },
  /** Active render orientation (drives the few scenes/widgets that must branch). */
  get orientation(): 'portrait' | 'landscape' {
    return _orientation;
  },
};

type Listener = () => void;
const listeners = new Set<Listener>();

/** Publish the live design-space size and notify subscribers. No-op if both dimensions are unchanged. */
export function setViewport(w: number, h: number): void {
  const nw = Math.round(w);
  const nh = Math.round(h);
  if (nw === _w && nh === _h) return;
  _w = nw;
  _h = nh;
  for (const fn of listeners) fn();
}

/**
 * Set the active scene's desired orientation (called by SceneManager from `scene.orientation`). Does
 * NOT notify directly — SceneManager forces a relayout right after, which calls {@link setViewport}.
 */
export function setActiveOrientation(o: 'portrait' | 'landscape'): void {
  _orientation = o;
}

/** Read the active orientation (PixiApp.layout() branches on this). */
export function activeOrientation(): 'portrait' | 'landscape' {
  return _orientation;
}

/** Top safe-area inset in screen px (read by PixiApp.layout). */
export function safeInsetTop(): number {
  return _insetTop;
}
/** Bottom safe-area inset in screen px (read by PixiApp.layout). */
export function safeInsetBottom(): number {
  return _insetBottom;
}
/**
 * Set the safe-area insets (screen px). Telegram reports these in fullscreen; call on init and on the
 * platform's safe-area change events, then nudge a `resize` so {@link PixiApp.layout} re-runs. No-op if
 * unchanged. Does NOT notify viewport listeners directly — the resulting relayout calls setViewport(),
 * which does, so scenes re-flow exactly once.
 */
export function setSafeInsets(topPx: number, bottomPx: number): void {
  const t = Math.max(0, Math.round(topPx));
  const b = Math.max(0, Math.round(bottomPx));
  if (t === _insetTop && b === _insetBottom) return;
  _insetTop = t;
  _insetBottom = b;
}

/** Subscribe to viewport-height changes (resize / orientation / Telegram fullscreen). Returns an unsubscribe. */
export function onViewportChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
